import type { ColDef, ICellRendererParams, IRowNode } from "ag-grid-enterprise";
import { Typography } from "antd";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import { ClassBadge } from "pages/design-scope-page/components/scope-ui";
import type {
  DesignScopeCatalogItem,
  DesignScopeCatalogItemClassification
} from "./apis";
import {
  CATALOG_TREE_SYSTEM_LABEL,
  CATALOG_UNCLASSIFIED,
  decodeCatalogSeg,
  type CatalogTreeSystem
} from "./catalog-items-hierarchy";

export type CatalogPrintRow = DesignScopeCatalogItem & { displayId: number };

function classificationForSystem(
  classifications: DesignScopeCatalogItemClassification[] | undefined,
  system: DesignScopeCatalogItemClassification["system"]
): DesignScopeCatalogItemClassification | null {
  return classifications?.find((item) => item.system === system) ?? null;
}

function classificationDisplayText(
  classifications: DesignScopeCatalogItemClassification[] | undefined,
  system: DesignScopeCatalogItemClassification["system"]
): string {
  const matches =
    classifications?.filter((item) => item.system === system) ?? [];
  return matches
    .map((item) => [item.code, item.name].filter(Boolean).join(" "))
    .filter(Boolean)
    .join(", ");
}

function ClassificationPrintCell(
  params: ICellRendererParams<CatalogPrintRow> & {
    system: DesignScopeCatalogItemClassification["system"];
  }
) {
  const { data, system } = params;
  if (!data) return null;
  const badgeSystem = system === "UNIFORMAT" ? "UF" : "MF";
  const matches = data.classifications.filter((item) => item.system === system);
  if (matches.length === 0) {
    return <Typography.Text type="secondary">—</Typography.Text>;
  }
  return (
    <>
      {matches.map((item) => (
        <span key={item.id} className="inline-flex min-w-0 items-center gap-2">
          <ClassBadge system={badgeSystem} code={item.code} />
          <Typography.Text className="min-w-0" ellipsis>
            {item.name || "—"}
          </Typography.Text>
        </span>
      ))}
    </>
  );
}

function idPrintColumn(): ColDef<CatalogPrintRow> {
  return {
    headerName: "ID",
    field: "displayId",
    colId: "displayId",
    width: 72,
    minWidth: 64,
    maxWidth: 96,
    flex: 0,
    suppressHeaderMenuButton: true
  };
}

function titlePrintColumn(): ColDef<CatalogPrintRow> {
  return {
    headerName: "Title",
    field: "title",
    colId: "title",
    flex: 2,
    minWidth: 180,
    valueGetter: (params) => params.data?.title ?? "",
    cellRenderer: (params: ICellRendererParams<CatalogPrintRow>) => {
      if (!params.data) return null;
      return params.data.title ? (
        <TruncatedTextWithTooltip text={params.data.title} dataTestId="title" />
      ) : (
        <Typography.Text type="secondary">—</Typography.Text>
      );
    }
  };
}

function descriptionPrintColumn(): ColDef<CatalogPrintRow> {
  return {
    headerName: "Description",
    field: "description",
    colId: "description",
    flex: 1.87,
    minWidth: 154,
    valueGetter: (params) => params.data?.description ?? "",
    cellRenderer: (params: ICellRendererParams<CatalogPrintRow>) => {
      if (!params.data) return null;
      return params.data.description ? (
        <TruncatedTextWithTooltip
          text={params.data.description}
          dataTestId="description"
        />
      ) : (
        <Typography.Text type="secondary">—</Typography.Text>
      );
    }
  };
}

function classificationPrintColumn(
  headerName: string,
  colId: "uniformat" | "masterformat",
  stored: DesignScopeCatalogItemClassification["system"]
): ColDef<CatalogPrintRow> {
  return {
    headerName,
    colId,
    flex: 2,
    minWidth: 220,
    valueGetter: (params) =>
      classificationDisplayText(params.data?.classifications, stored),
    cellRenderer: (params: ICellRendererParams<CatalogPrintRow>) => (
      <ClassificationPrintCell {...params} system={stored} />
    )
  };
}

/** Read-only list columns for PDF/Print (no Actions / editors). */
export function buildCatalogItemsListPrintColumnDefs(): ColDef<CatalogPrintRow>[] {
  return [
    idPrintColumn(),
    titlePrintColumn(),
    descriptionPrintColumn(),
    classificationPrintColumn("UniFormat", "uniformat", "UNIFORMAT"),
    classificationPrintColumn("MasterFormat", "masterformat", "MASTERFORMAT")
  ];
}

/** Read-only hierarchy columns for PDF/Print (no Actions / editors). */
export function buildCatalogItemsPrintColumnDefs(
  treeSystem: CatalogTreeSystem
): ColDef<CatalogPrintRow>[] {
  const otherStored =
    treeSystem === "UF" ? ("MASTERFORMAT" as const) : ("UNIFORMAT" as const);
  const otherColId = treeSystem === "UF" ? "masterformat" : "uniformat";
  const otherHeader = treeSystem === "UF" ? "MasterFormat" : "UniFormat";

  return [
    {
      headerName: "No. of Scope Items",
      colId: "scopeItemCount",
      suppressMovable: true,
      minWidth: 160,
      maxWidth: 200,
      filter: false,
      valueGetter: (params) => {
        if (!params.node || params.node.group !== true) return "";
        return (params.node.allLeafChildren ?? []).filter((n) => n.data).length;
      },
      cellRenderer: (params: ICellRendererParams<CatalogPrintRow>) => {
        if (!params.node?.group) return null;
        const count = (params.node.allLeafChildren ?? []).filter(
          (n) => n.data
        ).length;
        return (
          <span className="inline-flex items-center rounded-xl border border-solid border-ciq-gray-300 px-2 py-0.5 text-xs font-normal text-black text-opacity-50">
            {count}
          </span>
        );
      }
    },
    idPrintColumn(),
    titlePrintColumn(),
    descriptionPrintColumn(),
    classificationPrintColumn(otherHeader, otherColId, otherStored)
  ];
}

export function buildCatalogItemsPrintAutoGroupColumnDef(
  treeSystem: CatalogTreeSystem
): ColDef<CatalogPrintRow> {
  const stored =
    treeSystem === "UF" ? ("UNIFORMAT" as const) : ("MASTERFORMAT" as const);
  return {
    headerName: CATALOG_TREE_SYSTEM_LABEL[treeSystem],
    minWidth: 420,
    width: 420,
    flex: 2,
    valueGetter: (params) => {
      if (params.data) {
        return (
          classificationForSystem(params.data.classifications, stored)?.code ??
          ""
        );
      }
      const key = String(params.node?.key ?? "");
      if (key === CATALOG_UNCLASSIFIED) return "Unclassified";
      const seg = decodeCatalogSeg(key);
      return [seg.code, seg.title].filter(Boolean).join(" ");
    },
    cellRendererParams: {
      suppressCount: true,
      innerRenderer: (params: {
        data?: CatalogPrintRow;
        node?: IRowNode<CatalogPrintRow>;
      }) => {
        if (params.data) {
          const own = classificationForSystem(
            params.data.classifications,
            stored
          );
          if (!own?.code) return null;
          return <ClassBadge system={treeSystem} code={own.code} />;
        }
        if (!params.node) return null;
        const key = String(params.node.key ?? "");
        if (key === CATALOG_UNCLASSIFIED) {
          return (
            <Typography.Text strong type="secondary">
              Unclassified
            </Typography.Text>
          );
        }
        const seg = decodeCatalogSeg(key);
        return (
          <span className="inline-flex min-w-0 items-center gap-2">
            <ClassBadge system={seg.system} code={seg.code} />
            <Typography.Text strong className="min-w-0" ellipsis>
              {seg.title}
            </Typography.Text>
          </span>
        );
      }
    }
  };
}
